import { FINALIZING_MEETING_STATUSES, LIVE_MEETING_STATUSES, MEETING_STATUSES } from "@lexicanon/shared"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Alert, Badge, Container, Group, Stack, Title } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useEffect, useMemo, useRef, useState } from "react"
import { MeetingFinalizingView } from "../../../components/MeetingSummary/MeetingFinalizingView"
import { DetailSkeleton } from "../../../components/PageSkeletons"
import type { IAudioPlayerRef } from "../../../components/Player/interfaces"
import { RecordingControls } from "../../../components/RecordingControls"
import { SpeakerColorsProvider } from "../../../contexts/SpeakerColors"
import { useAuth } from "../../../hooks/useAuth"
import { useCompanyApi } from "../../../hooks/useCompanyApi"
import { useCompanyQuery } from "../../../hooks/useCompanyQuery"
import { useIsOrgAdmin } from "../../../hooks/useIsOrgAdmin"
import { useLiveMeeting } from "../../../hooks/useLiveMeeting"
import { useMeetingSpeakers } from "../../../hooks/useMeetingSpeakers"
import { useSpeakerHypothesisActions } from "../../../hooks/useSpeakerHypothesisActions"
import { fetchMeeting, fetchParticipants } from "../../../lib/api"
import { extractErrorMessage } from "../../../lib/api/errors"
import { displayMeetingTitle } from "../../../lib/meeting-title"
import { deriveEffectiveProgress } from "../../../lib/pipeline-progress"
import { queryKeys } from "../../../lib/query-keys"
import { speakerDisplayNameList } from "../../../lib/speaker-vm"
import { useProduct } from "../../../lib/tenant"
import type { IMeetingDetail } from "../../../lib/types/meeting"
import { MeetingDetailLegacy } from "./-meeting-detail/MeetingDetailLegacy"
import { MeetingDetailLexSummary } from "./-meeting-detail/MeetingDetailLexSummary"
import { MeetingDetailLite } from "./-meeting-detail/MeetingDetailLite"
import {
  deriveFinishingDisplay,
  isActivelyRecording as deriveIsActivelyRecording,
  isFinalizingLexMeeting,
  isIntermediateStatus,
  isLiteMeeting,
  isObserverMeeting,
  shouldShowFinalizing,
  shouldUseLexicanonSummary,
} from "./-meeting-detail/meeting-detail-mode"
import { useMeetingDetailEvents } from "./-meeting-detail/useMeetingDetailEvents"
import { useMeetingJargon } from "./-meeting-detail/useMeetingJargon"
import { useMeetingTranslation } from "./-meeting-detail/useMeetingTranslation"

// Re-exported from `@lexicanon/shared` so the set membership stays in lockstep
// with the canonical `TMeetingStatus` union — adding a new status is a single
// edit there, automatically picked up here.
const LIVE_STATUSES = LIVE_MEETING_STATUSES
// Post-recording processing phases — the finalize "wow" screen polls fast here.
// Canonical set lives in `@lexicanon/shared` so the list page shares membership.
const FINALIZING_STATUSES = FINALIZING_MEETING_STATUSES

/** How long a Lite meeting stays on "Done" after it flips to summarized. */
const LITE_DONE_HOLD_MS = 800

/**
 * True on the poll where a Lite meeting first becomes summarized. A direct
 * open of an already-summarized meeting (no previous status) does not hold.
 */
function liteFinishingJustFinished(previousStatus: string | null, status: string | null, isLite: boolean): boolean {
  return (
    isLite &&
    status === MEETING_STATUSES.SUMMARIZED &&
    previousStatus !== null &&
    previousStatus !== MEETING_STATUSES.SUMMARIZED &&
    previousStatus !== MEETING_STATUSES.ERROR
  )
}

function MeetingDetailPage() {
  const company = useCompanyApi()
  const product = useProduct()
  const { id } = Route.useParams()
  // Optional deep-link seek position (seconds) — set by search-result cards
  // linking to a transcript/quote moment. Positions the audio player on load.
  const { t: seekSeconds, tab: detailTab } = Route.useSearch()
  const { user: authUser } = useAuth()
  const orgId = authUser?.activeOrganizationId ?? null
  const queryClient = useQueryClient()
  const [isLive, setIsLive] = useState(false)
  const [accessOpened, { open: openAccess, close: closeAccess }] = useDisclosure(false)

  const {
    data: meeting,
    isLoading,
    error,
    refetch,
  } = useCompanyQuery({
    key: (org) => queryKeys.meeting(org, id),
    queryFn: (client) => fetchMeeting(client, id),
    // Poll snappily through the post-recording finalize phases (the "wow"
    // screen) so progress + the hand-off to the summary feel near-real-time;
    // fall back to a lazy poll while actively recording, and stop once done.
    refetchInterval: (query) => {
      const s = query.state.data?.status ?? ""
      if (FINALIZING_STATUSES.has(s)) return 2_500
      // Post-finish `in_progress` (transcription, before the pipeline starts)
      // must poll fast too, or the finalize bar sits on the first step for up to
      // a full 15s. During an ACTIVE local recording the live WS/SSE drives the
      // UI, so keep the lazy cadence there and don't hammer the row.
      if (s === MEETING_STATUSES.IN_PROGRESS) {
        const recordingHere = sessionStorage.getItem("lexicanon:activeRecording") === id
        return recordingHere ? 15_000 : 2_500
      }
      return LIVE_STATUSES.has(s) ? 15_000 : false
    },
    select(data) {
      if (!data) return data
      if (LIVE_STATUSES.has(data.status) !== isLive) {
        setIsLive(LIVE_STATUSES.has(data.status))
      }
      return data
    },
  })

  // Per-meeting jargon suggestions + accept/dismiss flow (colocated hook).
  const { jargonSuggestions, jargonBusyId, onAcceptJargon, onDismissJargon } = useMeetingJargon(
    company,
    orgId,
    id,
    isLive,
    {
      meetingLoaded: meeting != null,
      isLite: meeting != null && isLiteMeeting(meeting),
    },
  )

  const { processingStage, pipeline, justFinalizedRef, showSummary } = useMeetingDetailEvents(
    id,
    orgId,
    product,
    meeting,
    setIsLive,
  )

  const navigate = Route.useNavigate()
  const navigateHome = (): void => {
    void navigate({ to: "/" })
  }

  const companyId = authUser?.activeOrganizationId
  // Platform admin OR active-org owner/admin — mirrors the backend's
  // `isOrgOwnerOrAdmin` gate. The session only carries the *platform* isAdmin
  // flag, so the org role comes from Better Auth's active member (useIsOrgAdmin).
  const isOrgAdmin = useIsOrgAdmin()
  const isOrganizer = meeting?.organizer_email.toLowerCase() === (authUser?.email ?? "").toLowerCase()
  const canManageAccess = isOrgAdmin || isOrganizer

  // Observer live view — must be called unconditionally (Rules of Hooks).
  // Passes empty args when not observing; the hook short-circuits when companyId is null.
  const isObserverCandidate = meeting?.status === MEETING_STATUSES.IN_PROGRESS && meeting?.source === "browser"
  const liveData = useLiveMeeting(isObserverCandidate ? id : "", isObserverCandidate ? companyId : null)
  // Live speaker-recognition confirm/reject for the observer strip — the single
  // owner (pending keys + cross-surface cache invalidation), shared with the
  // recorder + post-meeting review paths. Must be called unconditionally.
  const { pending: hypothesisPending, onAction: onHypothesisAction } = useSpeakerHypothesisActions(id)

  const participantsQuery = useCompanyQuery({
    key: (org) => queryKeys.ttrpgParticipants(org, id),
    queryFn: (client) => fetchParticipants(client, id),
    enabled: !!meeting?.series_id,
  })

  const myRole =
    participantsQuery.data?.find((p) => p.userId === authUser?.id)?.role ?? (authUser?.isAdmin ? "dm" : null)

  const audioPlayerRef = useRef<IAudioPlayerRef>(null)

  // Per-meeting language switcher (Phase 4 of multi-language) — colocated hook.
  const {
    sourceLocale,
    analysisLocale,
    availableTranslations,
    displayAnalysis,
    isTranslating,
    hasTranslationError,
    onLocaleChange,
  } = useMeetingTranslation(company, orgId, id, meeting)

  // One derivation for every speaker surface. `useMeetingSpeakers` maps the
  // backend's unified `speakers[]` read model when present, else reproduces the
  // legacy roster ∪ analysis derivation that used to live inline here. It hands
  // back the render-ready VMs (the panel), the name→colour map (the Provider +
  // Player + cards), and the resolved-name list the summary cards still consume.
  const speakerVMs = useMeetingSpeakers(meeting)
  const speakerColorMap = speakerVMs.colorMap
  const { i18n } = useLingui()
  // Display labels for the summary cards ("Speaker 4", translated), one per VM in
  // order. Not deduped — talk share is zipped by index. The raw roster key stays
  // on the VM for mutations.
  const speakerNameList = useMemo(() => speakerDisplayNameList(speakerVMs.speakers, i18n), [speakerVMs, i18n])

  // Lite has no analysis, so `justFinalized` stays set and must not hold this
  // screen. When the status flips to summarized, keep "Done" up briefly, then
  // let the meeting page take over. The status compare happens during render so
  // the page does not flash for a frame before the effect runs.
  const meetingStatus = meeting?.status ?? null
  const meetingIsLite = meeting != null && isLiteMeeting(meeting) && product === "lexicanon"
  const [liteDoneHold, setLiteDoneHold] = useState(false)
  const [trackedMeetingStatus, setTrackedMeetingStatus] = useState<string | null>(meetingStatus)
  if (meetingStatus !== trackedMeetingStatus) {
    const justFinished = liteFinishingJustFinished(trackedMeetingStatus, meetingStatus, meetingIsLite)
    setTrackedMeetingStatus(meetingStatus)
    if (justFinished) setLiteDoneHold(true)
    else if (meetingStatus !== MEETING_STATUSES.SUMMARIZED) setLiteDoneHold(false)
  }
  useEffect(() => {
    if (!liteDoneHold) return
    const timer = window.setTimeout(() => setLiteDoneHold(false), LITE_DONE_HOLD_MS)
    return () => window.clearTimeout(timer)
  }, [liteDoneHold])

  // Provisional meeting: created offline, not yet on the server.
  // We check sessionStorage: if the key still exists the meeting hasn't been
  // created on the server yet — show the recording UI immediately.
  // Once the reconnect effect creates the meeting it removes the key, so the
  // next render falls through to the normal fetch path and shows full detail.
  const provisionalStored = id.startsWith("provisional_") ? sessionStorage.getItem(`provisional:${id}`) : null

  if (provisionalStored) {
    const provisionalData = JSON.parse(provisionalStored) as { subject: string; templateId?: string }
    return (
      <Container size="lg" py="md">
        <Stack gap="md">
          <Group>
            <Title order={3}>
              {displayMeetingTitle(provisionalData.subject, null, i18n, {
                lite: isLiteMeeting({ template_id: provisionalData.templateId ?? null }),
              })}
            </Title>
            <Badge color="warning" variant="light">
              <Trans>Offline recording</Trans>
            </Badge>
          </Group>
          <RecordingControls
            meetingId={id}
            subject={provisionalData.subject}
            templateId={provisionalData.templateId}
            isProvisional
            onRecordingStopped={() => navigate({ to: "/meetings/$id", params: { id } })}
          />
        </Stack>
      </Container>
    )
  }

  if (isLoading || !company) {
    return (
      <Container size="lg" py="xl">
        <DetailSkeleton />
      </Container>
    )
  }
  if (error && !meeting) {
    return <Alert color="danger">{extractErrorMessage(error)}</Alert>
  }
  if (!meeting) {
    return (
      <Container size="lg" py="xl">
        <DetailSkeleton />
      </Container>
    )
  }

  const isThisTabRecording = sessionStorage.getItem("lexicanon:activeRecording") === id
  // "detected" means a meeting exists but nobody has streamed yet — show the
  // recording UI so the user can start. `recovering` is NOT a recordable state:
  // it's the backend finalizing a meeting via the recovery path, so it must fall
  // through to the finalize surface, never the recording controls. Only gate on
  // sessionStorage for "in_progress" (another tab may already be streaming audio)
  // to avoid showing duplicate controls.
  const isIntermediate = isIntermediateStatus(meeting.status)
  const isActivelyRecording = deriveIsActivelyRecording(meeting, isThisTabRecording)
  const isObserver = isObserverMeeting(meeting, isThisTabRecording)
  const isLite = isLiteMeeting(meeting)

  // Use the new MeetingSummaryView for Lexicanon post-recording. Voxcanon stays on
  // the legacy detail layout — its TTRPG-specific affordances (TTRPGPanel,
  // SceneInsightsDrawer, TTRPGReports, character chips) aren't part of the
  // Lexicanon design and need their own surface.
  const useLexicanonSummary = shouldUseLexicanonSummary({
    product,
    isActivelyRecording,
    isIntermediate,
    hasAnalysis: !!meeting.analysis,
    hasDisplayAnalysis: !!displayAnalysis,
    isLite,
  })

  // Finalize progress: the live per-DAG-step signal (when the pipeline is
  // running) wins over the coarse SSE/persisted stage — it gives the true step
  // count, the running step's stage for highlighting, and its name for the
  // subtitle — falling back to the persisted baseline so a missed/late SSE event
  // never leaves the bar stuck on step 1. Progress floors to the active stage so
  // an instant stage bump lifts the bar without waiting for the next poll. All of
  // that math lives in `lib/pipeline-progress` now so the list page shares it.
  const {
    stage: effectiveStage,
    progress: effectiveProgress,
    currentStepName,
  } = deriveEffectiveProgress({
    pipeline,
    sseStage: processingStage,
    sseProgress: null,
    persistedStage: meeting.processing_stage,
    persistedProgress: meeting.processing_progress,
  })

  // Lexicanon: hold the finalizing screen from "finish" until the summary is
  // actually ready, so we never flash the bare recording waveform during the
  // hand-off. The exit condition is keyed off the meeting's *terminal status*,
  // never the live SSE stage: `processing_stage` hits "complete" a poll-interval
  // before the analysis blob lands, so gating on the stage would tear the
  // finalize surface down and flash the bare page in that gap. Instead we hold
  // for any still-processing phase, and only let go once the meeting is terminal
  // — `summarized` (→ the summary takes over) or, in the rare clobbered case,
  // `summarized`/`error` with no analysis (→ fall through to the re-runnable
  // "No analysis available yet." surface instead of spinning forever).
  const isFinalizingLex = isFinalizingLexMeeting({
    product,
    isActivelyRecording,
    useLexicanonSummary,
    status: meeting.status,
    analysis: meeting.analysis,
    recordingUrl: meeting.recording_url,
    isIntermediate,
    isLite,
  })

  // Show the finalizing screen while finalizing, and hold it for the brief tick
  // between the analysis arriving and the crossfade into the summary (so we
  // never flash the bare waveform). `justFinalizedRef`/`showSummary` are driven
  // by the effect in `useMeetingDetailEvents`.
  const showFinalizing =
    shouldShowFinalizing({
      isFinalizingLex,
      useLexicanonSummary,
      justFinalized: justFinalizedRef.current,
      showSummary,
    }) || liteDoneHold
  if (showFinalizing) {
    // Full shows "Done" only once the analysis has landed. Until then a
    // premature `complete` stays on the summary step. Lite has no analysis:
    // it reaches Done when the meeting is summarized (held briefly via
    // `liteDoneHold`) or the backend stage is already complete.
    const { finishingStage, finishingProgress } = deriveFinishingDisplay({
      analysisReady: useLexicanonSummary,
      effectiveStage,
      isIntermediate,
      effectiveProgress,
      isLite,
      status: meeting.status,
    })
    return (
      <SpeakerColorsProvider colors={speakerColorMap}>
        <Container size="lg" py="xl">
          <MeetingFinalizingView
            subject={displayMeetingTitle(meeting.subject, meeting.topics, i18n, {
              lite: isLite,
              startedAt: meeting.start_time,
            })}
            startTime={meeting.start_time}
            speakerNames={meeting.speaker_names}
            recordingUrl={meeting.recording_url}
            segments={meeting.streaming_segments}
            speakerColors={speakerColorMap}
            chapters={meeting.chapters}
            stage={finishingStage}
            progress={finishingProgress}
            currentStepName={isIntermediate ? currentStepName : null}
            templateId={meeting.template_id}
          />
        </Container>
      </SpeakerColorsProvider>
    )
  }

  // The deliberate share list (ADR 0056) — drives the "+N" badge on Manage access.
  const sharedWithEmails: string[] = (() => {
    try {
      const parsed: unknown = JSON.parse(meeting.shared_with_emails ?? "[]")
      return Array.isArray(parsed) ? parsed.filter((e): e is string => typeof e === "string") : []
    } catch {
      return []
    }
  })()

  const detailMeeting = meeting as unknown as IMeetingDetail & { attendee_emails?: string }

  if (isLite && !isActivelyRecording && !isObserver && !showFinalizing) {
    return (
      <MeetingDetailLite
        meeting={detailMeeting}
        meetingId={id}
        orgId={orgId}
        company={company}
        companyId={companyId ?? undefined}
        queryClient={queryClient}
        speakerVMs={speakerVMs}
        speakerColorMap={speakerColorMap}
        canManageAccess={canManageAccess}
        isOrgAdmin={isOrgAdmin}
        isOrganizer={isOrganizer}
        accessOpened={accessOpened}
        openAccess={openAccess}
        closeAccess={closeAccess}
        refetch={refetch}
        navigateHome={navigateHome}
        experienceMode={authUser?.experienceMode}
      />
    )
  }

  if (useLexicanonSummary && (showSummary || !justFinalizedRef.current) && displayAnalysis) {
    return (
      <MeetingDetailLexSummary
        meeting={detailMeeting}
        meetingId={id}
        orgId={orgId}
        company={company}
        companyId={companyId ?? undefined}
        queryClient={queryClient}
        speakerNameList={speakerNameList}
        speakerVMs={speakerVMs}
        speakerColorMap={speakerColorMap}
        displayAnalysis={displayAnalysis}
        accessOpened={accessOpened}
        closeAccess={closeAccess}
        openAccess={openAccess}
        canManageAccess={canManageAccess}
        isOrgAdmin={isOrgAdmin}
        isOrganizer={isOrganizer}
        reveal={justFinalizedRef.current}
        seekSeconds={seekSeconds}
        initialTab={detailTab}
        sourceLocale={sourceLocale}
        analysisLocale={analysisLocale}
        availableTranslations={availableTranslations}
        isTranslating={isTranslating}
        hasTranslationError={hasTranslationError}
        onLocaleChange={onLocaleChange}
        jargonSuggestions={jargonSuggestions}
        onAcceptJargon={onAcceptJargon}
        onDismissJargon={onDismissJargon}
        jargonBusyId={jargonBusyId}
        refetch={refetch}
        navigateHome={navigateHome}
        experienceMode={authUser?.experienceMode}
      />
    )
  }

  // The Lexicanon recording surface (pre-record + live) is a full-bleed layout
  // that owns its own gutters (RecordingSurface/.shell). Render it WITHOUT the
  // page <Container> so it isn't double-padded on mobile — matching the
  // fullscreen Storybook story. Voxcanon keeps the container-wrapped layout.
  if (isActivelyRecording && product === "lexicanon") {
    return (
      <SpeakerColorsProvider colors={speakerColorMap}>
        <RecordingControls
          meetingId={id}
          onRecordingStopped={() => refetch()}
          meetingSubject={meeting.subject}
          meetingStartTime={meeting.start_time ?? null}
        />
      </SpeakerColorsProvider>
    )
  }

  return (
    <MeetingDetailLegacy
      meeting={detailMeeting}
      meetingId={id}
      orgId={orgId}
      company={company}
      companyId={companyId ?? undefined}
      queryClient={queryClient}
      speakerVMs={speakerVMs}
      speakerColorMap={speakerColorMap}
      displayAnalysis={displayAnalysis}
      accessOpened={accessOpened}
      closeAccess={closeAccess}
      openAccess={openAccess}
      canManageAccess={canManageAccess}
      isOrgAdmin={isOrgAdmin}
      isOrganizer={isOrganizer}
      isActivelyRecording={isActivelyRecording}
      isObserver={isObserver}
      isIntermediate={isIntermediate}
      sharedWithEmails={sharedWithEmails}
      seekSeconds={seekSeconds}
      sourceLocale={sourceLocale}
      analysisLocale={analysisLocale}
      availableTranslations={availableTranslations}
      isTranslating={isTranslating}
      hasTranslationError={hasTranslationError}
      onLocaleChange={onLocaleChange}
      liveData={liveData}
      hypothesisPending={hypothesisPending}
      onHypothesisAction={onHypothesisAction}
      audioPlayerRef={audioPlayerRef}
      effectiveStage={effectiveStage}
      effectiveProgress={effectiveProgress}
      currentStepName={currentStepName}
      myRole={myRole}
      refetch={refetch}
      navigateHome={navigateHome}
    />
  )
}

export const Route = createFileRoute("/_auth/meetings/$id")({
  component: MeetingDetailPage,
  // `?t=<seconds>` deep-links the audio player. `?tab=details` opens the
  // narrow-layout Details tab (Summary is the default when `tab` is absent).
  validateSearch: (search: Record<string, unknown>): { t?: number; tab?: "summary" | "details" } => {
    const raw = search.t
    const n = typeof raw === "number" ? raw : typeof raw === "string" ? Number(raw) : Number.NaN
    const tab = search.tab === "summary" || search.tab === "details" ? search.tab : undefined
    return {
      ...(Number.isFinite(n) && n >= 0 ? { t: n } : {}),
      ...(tab ? { tab } : {}),
    }
  },
})
